import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import gql from 'graphql-tag';
import qs from 'qs';
import { RouteComponentProps } from 'react-router-dom';
import { useQuery } from 'react-apollo';
import { AppointmentType, theme, ReduxState } from 'kb-shared';
import Loader from 'components/Loader';
import OptionsList from './components/OptionsList';
import Question from './components/Question';
import { ReactComponent as FertilityIcon } from '../../assets/icons/fertility.svg';
import { ReactComponent as GynIcon } from '../../assets/icons/gyn.svg';
import { ReactComponent as WellnessIcon } from '../../assets/icons/wellness.svg';
import { ReactComponent as MaternityIcon } from '../../assets/icons/maternity.svg';

import AuthFlowLayout from '../../components/AuthFlowLayout';
import BubbleStepTracker from '../../components/BubbleStepTracker';
import QuizResult from './components/QuizResult';
import BackButton from '../../components/Button/BackButton';
import { leftArrow2 } from '../../../src/assets/icons';
import EmployerBenefitCTA from './components/EmployerBenefitCTA';

export type BookingQuizCategory = 'fertility' | 'gynecology' | 'wellness';

export interface BookingQuizAnswer {
  id: number;
  text: string;
  category?: BookingQuizCategory;
  appointmentTypes: Array<AppointmentType>;
  appointmentTypeIds: Array<number>;
}

export const CATEGORY_ICONS = {
  fertility: <FertilityIcon />,
  gynecology: <GynIcon />,
  wellness: <WellnessIcon />,
  maternity: <MaternityIcon />
};

const QUIZ_QUESTION_QUERY = gql`
  query bookingQuizQuestion($parentBookingQuizAnswerId: ID) {
    bookingQuizQuestions(parentBookingQuizAnswerId: $parentBookingQuizAnswerId) {
      id
      category
      text
      parentBookingQuizAnswerId
      childBookingQuizAnswers {
        id
        category
        text
        parentBookingQuizQuestionId
        appointmentTypeIds
      }
    }
  }
`;

const QUIZ_ANSWER_QUERY = gql`
  query bookingQuizAnswer($id: Int!) {
    bookingQuizAnswer(id: $id) {
      id
      category
      text
      parentBookingQuizQuestionId
      appointmentTypeIds
      appointmentTypes {
        id
        name
        description
        virtual
      }
    }
  }
`;

type Props = RouteComponentProps & {
  isLoggedIn: boolean;
};

const BookingQuiz = ({ history, isLoggedIn }: Props) => {
  const params = qs.parse(history.location.search, { ignoreQueryPrefix: true }) as any;
  const { q: currentQParam, a: currentAParam } = params;
  const [parentBookingQuizAnswerId, setParentBookingQuizAnswerId] = useState<number | null>(
    currentQParam || null
  );
  const [selectedAnswerId, setSelectedAnswerId] = useState<number | null>(currentAParam || null);
  const { data } = useQuery(QUIZ_QUESTION_QUERY, {
    variables: { parentBookingQuizAnswerId }
  });
  const { data: selectedAnswerData, loading: answerLoading } = useQuery(QUIZ_ANSWER_QUERY, {
    variables: { id: Number(selectedAnswerId) },
    skip: !selectedAnswerId
  });
  const currentQuestion = data?.bookingQuizQuestions;
  const appointmentTypes = selectedAnswerData?.bookingQuizAnswer?.appointmentTypes || [];
  const category = selectedAnswerData?.bookingQuizAnswer?.category;

  const stepNames = ['Services', 'Location', 'Sign up', 'Confirm'];

  if (isLoggedIn) delete stepNames[2];

  function handleResponse(quizAnswer: BookingQuizAnswer) {
    if (window.analytics) {
      window.analytics.track('Appointment Quiz Responded', {
        // eslint-disable-next-line @typescript-eslint/camelcase
        appointment_category: currentQuestion.category,
        // eslint-disable-next-line @typescript-eslint/camelcase
        question_text: currentQuestion.text,
        // eslint-disable-next-line @typescript-eslint/camelcase
        question_id: currentQuestion.id,
        // eslint-disable-next-line @typescript-eslint/camelcase
        option_text_selected: quizAnswer.text,
        // eslint-disable-next-line @typescript-eslint/camelcase
        option_id_selected: quizAnswer.id
      });
    }
    if (quizAnswer.appointmentTypeIds.length > 0) {
      history.push(`/booking-quiz?a=${quizAnswer.id}`);
    } else {
      history.push(`/booking-quiz?q=${quizAnswer.id}`);
    }
  }

  useEffect(() => {
    if (window.analytics) {
      window.analytics.page('Appointment Booking Quiz');
    }
  }, []);

  useEffect(() => {
    if (`${parentBookingQuizAnswerId}` !== currentQParam) {
      setParentBookingQuizAnswerId(currentQParam || null);
    }
  }, [parentBookingQuizAnswerId, currentQParam]);

  useEffect(() => {
    if (`${selectedAnswerId}` !== currentAParam) {
      setSelectedAnswerId(currentAParam || null);
    }
  }, [selectedAnswerId, currentAParam]);

  return (
    <AuthFlowLayout
      title={null}
      renderAboveTitleContent={() => {
        return (
          <>
            {!isLoggedIn && <EmployerBenefitCTA />}
            <BubbleStepTrackerWrapper>
              <BubbleStepTracker steps={stepNames} selectedStep={0} onSelectStep={() => {}} />
            </BubbleStepTrackerWrapper>
          </>
        );
      }}
      renderMainContent={() => {
        if (!currentQuestion || answerLoading) {
          return <Loader color={theme.colors.KBYellow} height={300} width={300} />;
        }

        if (appointmentTypes.length > 0) {
          return (
            <QuizResult
              appointmentTypes={appointmentTypes}
              category={category}
              isLoggedIn={isLoggedIn}
            />
          );
        }

        return (
          <QuizContainer>
            <Question text={currentQuestion.text} category={currentQuestion.category} />
            <OptionsList
              options={currentQuestion.childBookingQuizAnswers}
              large={!currentQuestion.parentBookingQuizAnswerId}
              onSelect={handleResponse}
              isLoggedIn={isLoggedIn}
            />
            {currentQuestion.category && (
              <BackButton
                style={{ alignSelf: 'start', marginTop: '85px' }}
                onClick={() => history.goBack()}
              >
                <ArrowIcon src={leftArrow2} />
                Back
              </BackButton>
            )}
          </QuizContainer>
        );
      }}
      renderDesktopRightSidebar={() => null}
    />
  );
};

const mapStateToProps = ({ patient: { isLoggedIn } }: ReduxState) => ({
  isLoggedIn: isLoggedIn
});

export default connect(mapStateToProps, null)(BookingQuiz);

const QuizContainer = styled.div`
  width: 100%;
`;

const BubbleStepTrackerWrapper = styled.div`
  padding-bottom: 24px;
  display: flex;
  justify-content: center;
`;

const ArrowIcon = styled.img`
  height: 12px;
  width: 40px;
  @media screen and (max-width: ${theme.breakpoints.largePhonePortrait}) {
    margin-right: -14px;
  }
`;
